import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const PLAN_LADDER = ["free", "bloq_plus", "circle_pro"];
const PLAN_LABEL = { bloq_plus: "BLOQ+", circle_pro: "Circle Pro", business: "Business" };

// Subtle usage meter for a plan-capped resource, sourced from GET /me/limits (shared ["my-limits"] cache).
// Forward-compatible: renders NOTHING until the backend ships the slot, and hides when the resource is
// unlimited (max == null). The upgrade CTA names the exact unlock (next paid tier + next_max) and
// deep-links straight to that plan's checkout on /app/membership (?upgrade=<plan>).
export function LimitMeter({ slot, label, unit, testid = "limit-meter", className }) {
  const navigate = useNavigate();
  const { data: limits } = useQuery({ queryKey: ["my-limits"], queryFn: api.myLimits, staleTime: 60000 });
  const data = limits?.[slot];
  if (!data || typeof data.max !== "number") return null; // slot absent or unlimited (max: null) → hide

  const used = data.owned ?? data.joined ?? 0;
  const max = data.max;
  const pct = Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
  const atCap = used >= max;
  const nextMax = limits?.next_max?.[slot];

  // Resolve the next paid tier above the user's current plan (the one that lifts this cap).
  const curIdx = PLAN_LADDER.indexOf(limits?.plan || "free");
  const targetPlan = curIdx >= 0 && curIdx < PLAN_LADDER.length - 1 ? PLAN_LADDER[curIdx + 1] : null;
  const targetName = targetPlan ? PLAN_LABEL[targetPlan] || "a paid plan" : null;
  const hasGain = typeof nextMax === "number" ? nextMax > max : true; // no next_max → assume a paid tier still helps
  const showUpgrade = !!targetPlan && hasGain && pct >= 60; // nudge as they approach the cap

  const noun = unit || label;
  const ctaText =
    typeof nextMax === "number"
      ? `Upgrade to ${targetName} for ${nextMax} ${noun}`
      : `Upgrade to ${targetName}`;
  const goUpgrade = () => navigate(`/app/membership?upgrade=${targetPlan}&ref=limit_${slot}`);

  return (
    <div
      className={cn(
        "rounded-xl border px-3 py-2",
        atCap ? "border-amber-400/30 bg-amber-400/5" : "border-white/8 bg-bloq-s2",
        className,
      )}
      data-testid={testid}
    >
      <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-[11px]">
        <span className="text-bloq-muted">
          <b className={atCap ? "text-amber-300" : "text-white"}>{used}</b>
          <span className="text-bloq-muted">/</span>
          <b className="text-white">{max}</b> {label}
        </span>
        {showUpgrade && (
          <button
            type="button"
            onClick={goUpgrade}
            className="inline-flex items-center gap-1 font-semibold text-bloq-mint transition-opacity hover:opacity-80"
            data-testid={`${testid}-upgrade`}
          >
            <Sparkles className="h-3 w-3" />
            {ctaText}
          </button>
        )}
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-white/10">
        <div
          className={cn("h-full rounded-full transition-all", atCap ? "bg-amber-400" : "bg-bloq-mint")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
